// frontend/src/pages/TermsOfServicePage.tsx
import { Link } from 'react-router-dom';
import { InfoPageLayout } from './InfoPageLayout';

export function TermsOfServicePage() {
  return (
    <InfoPageLayout title="Terms of Service" metaDescription="Llambada terms of service — rules for using the platform, mini-app ownership, 70/30 creator revenue share, and acceptable use policies.">
      <p>Last Updated: February 3, 2026</p>

      <h2>1. Acceptance of Terms</h2>
      <p>
        By accessing or using Llambada ("the Service"), operated by <a href="https://chapterpal.com" target="_blank" rel="noopener noreferrer" className="text-accent-500 hover:underline">ChapterPal Inc</a>. ("we," "our," or "us"), 
        you agree to be bound by these Terms of Service ("Terms"). If you do not agree to all of these Terms, 
        do not use the Service.
      </p>

      <h2>2. Description of Service</h2>
      <p>
        Llambada is a platform that enables users to create, customize, and share software applications 
        ("Mini-Apps") using artificial intelligence. Users describe their desired functionality in natural 
        language, and AI systems generate the underlying code. Users can keep Mini-Apps private for personal 
        use or publish them for others to use.
      </p>

      <h2>3. User Accounts</h2>
      <p>
        You must be at least 18 years old to create an account and use the Service. You are responsible for 
        maintaining the security of your account and password. <a href="https://chapterpal.com" target="_blank" rel="noopener noreferrer" className="text-accent-500 hover:underline">ChapterPal Inc</a>. cannot and will not be 
        liable for any loss or damage from your failure to comply with this security obligation.
      </p>
      <p>
        You are responsible for all activities that occur under your account, including Mini-Apps created, 
        API keys configured, and any interactions with other users' Mini-Apps.
      </p>

      <h2>4. User Content and Mini-Apps</h2>
      
      <h3>4.1 Your Content</h3>
      <p>
        You retain ownership of all content you provide to the Service, including descriptions, prompts, 
        and any code you manually write or modify ("User Content"). You also retain ownership of the 
        Mini-Apps created through the Service based on your instructions.
      </p>

      <h3>4.2 License to <a href="https://chapterpal.com" target="_blank" rel="noopener noreferrer" className="text-accent-500 hover:underline">ChapterPal Inc</a>.</h3>
      <p>
        By submitting User Content and creating Mini-Apps on the Service, you grant <a href="https://chapterpal.com" target="_blank" rel="noopener noreferrer" className="text-accent-500 hover:underline">ChapterPal Inc</a>. a 
        worldwide, non-exclusive, royalty-free license to host, store, run, reproduce, modify, and display 
        your User Content and Mini-Apps solely to provide, secure, and improve the Service. This includes:
      </p>
      <ul>
        <li>Running your Mini-Apps on our infrastructure</li>
        <li>Processing your content with third-party AI models to generate code</li>
        <li>Displaying your published Mini-Apps to other users</li>
        <li>Creating backups and maintaining system security</li>
      </ul>

      <h3>4.3 Published Mini-Apps</h3>
      <p>
        If you choose to publish a Mini-App publicly, you grant additional rights for other users to 
        access and use your Mini-App, and to create customized versions ("forks") of your Mini-App. 
        You represent that you have the right to grant these permissions.
      </p>

      <h3>4.4 AI-Generated Code</h3>
      <p>
        <strong>Important:</strong> Code generated by AI systems through the Service is provided "as is" 
        without any warranty. You acknowledge that:
      </p>
      <ul>
        <li>AI-generated code may contain errors, bugs, security vulnerabilities, or produce unexpected results</li>
        <li>You are solely responsible for reviewing, testing, and validating any AI-generated code before use</li>
        <li>We make no representations about the fitness, security, or correctness of AI-generated code</li>
        <li>AI-generated code may inadvertently resemble existing code or infringe third-party rights</li>
      </ul>

      <h3>4.5 Your Responsibilities</h3>
      <p>You represent and warrant that:</p>
      <ul>
        <li>You have the right to provide all User Content you submit</li>
        <li>Your Mini-Apps do not infringe any third-party intellectual property rights</li>
        <li>Your Mini-Apps comply with all applicable laws and these Terms</li>
        <li>You will not use the Service to create Mini-Apps that are harmful, illegal, or violate others' rights</li>
      </ul>

      <h2>5. Acceptable Use</h2>
      <p>You agree not to use the Service to:</p>
      <ul>
        <li>Create Mini-Apps that are unlawful, harmful, threatening, abusive, harassing, defamatory, or invasive of privacy</li>
        <li>Create Mini-Apps that collect user data without proper disclosure and consent</li>
        <li>Create Mini-Apps containing malware, viruses, or malicious code</li>
        <li>Create Mini-Apps that infringe intellectual property rights</li>
        <li>Create Mini-Apps that facilitate illegal activities</li>
        <li>Attempt to reverse engineer, decompile, or derive source code from the Service's proprietary systems</li>
        <li>Circumvent technical protections, rate limits, or usage restrictions</li>
        <li>Use the Service to develop competing AI code generation services</li>
        <li>Scrape, harvest, or collect data from the Service without permission</li>
        <li>Impersonate others or misrepresent your affiliation</li>
      </ul>
      <p>
        We reserve the right to remove any Mini-App or User Content that violates these Terms, and to 
        suspend or terminate accounts of repeat violators.
      </p>

      <h2>6. API Keys and Third-Party Services</h2>
      
      <h3>6.1 Your API Keys</h3>
      <p>
        The Service allows you to provide your own API keys for third-party AI services (such as Anthropic). 
        When you provide your own API key:
      </p>
      <ul>
        <li>You are responsible for all charges incurred through your API key</li>
        <li>You must comply with the third party's terms of service</li>
        <li>We store your API key securely but cannot guarantee absolute security</li>
        <li>You should use API keys with appropriate usage limits</li>
      </ul>

      <h3>6.2 Platform API Credits</h3>
      <p>
        We provide limited free AI credits for creating Mini-Apps. These credits are subject to fair use 
        limits and may be modified or discontinued at any time.
      </p>

      <h2>7. Subscriptions, Payments, and Revenue Sharing</h2>

      <h3>7.1 Subscription Plans</h3>
      <p>
        Certain features of the Service require a paid subscription. Paid services are billed in advance 
        on a subscription basis and auto-renew at the end of each billing period until canceled.
      </p>

      <h3>7.2 Publishing Requirements</h3>
      <p>
        Only users with a paid subscription may publish Mini-Apps publicly and participate in the 
        revenue sharing program. Free users may create unlimited private Mini-Apps for personal use.
      </p>

      <h3>7.3 Revenue Sharing</h3>
      <p>
        When paid subscribers use your published Mini-Apps, you may earn a share of the attributed revenue:
      </p>
      <ul>
        <li><strong>Creator Share:</strong> 70% of attributed revenue</li>
        <li><strong>Platform Share:</strong> 30% retained by <a href="https://chapterpal.com" target="_blank" rel="noopener noreferrer" className="text-accent-500 hover:underline">ChapterPal Inc</a>.</li>
      </ul>
      <p>
        Revenue attribution, calculation methods, and minimum payout thresholds are determined by us and 
        may be modified with notice. Payouts are processed monthly via Stripe Connect, subject to 
        Stripe's terms and applicable tax requirements.
      </p>

      <h3>7.4 Cancellation and Refunds</h3>
      <p>
        You may cancel your subscription at any time via your account settings. Cancellations take effect 
        at the end of the current billing period. Subscription fees are generally non-refundable except 
        as required by applicable law.
      </p>

      <h2>8. Intellectual Property</h2>
      <p>
        The Service, including its original content, features, functionality, branding, and underlying 
        technology (excluding User Content and Mini-Apps), is and remains the exclusive property of 
        <a href="https://chapterpal.com" target="_blank" rel="noopener noreferrer" className="text-accent-500 hover:underline">ChapterPal Inc</a>. and its licensors. The Service is protected by copyright, trademark, and 
        other intellectual property laws.
      </p>

      <h2>9. Third-Party Services</h2>
      <p>
        The Service utilizes third-party services including but not limited to Supabase for infrastructure, 
        Stripe for payments, and AI providers (such as Anthropic) for code generation. Your use of these 
        integrated services is subject to their respective terms and privacy policies.
      </p>

      <h2>10. Disclaimers and Limitation of Liability</h2>

      <h3>10.1 Service Provided "As Is"</h3>
      <p>
        THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR 
        IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR 
        PURPOSE, NON-INFRINGEMENT, OR AVAILABILITY.
      </p>

      <h3>10.2 AI and Code Disclaimers</h3>
      <p>
        WE DO NOT WARRANT THAT AI-GENERATED CODE WILL BE ERROR-FREE, SECURE, OR SUITABLE FOR ANY 
        PARTICULAR PURPOSE. MINI-APPS MAY MALFUNCTION, PRODUCE INCORRECT RESULTS, OR CAUSE DATA LOSS. 
        YOU USE AI-GENERATED CODE AND MINI-APPS AT YOUR OWN RISK.
      </p>

      <h3>10.3 Limitation of Liability</h3>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, TRUE POSITIVE INC. AND ITS OFFICERS, DIRECTORS, 
        EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, 
        OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, OR GOODWILL, 
        ARISING FROM YOUR USE OF THE SERVICE OR ANY MINI-APPS.
      </p>
      <p>
        OUR TOTAL LIABILITY FOR ANY CLAIMS ARISING FROM THESE TERMS OR YOUR USE OF THE SERVICE SHALL 
        NOT EXCEED THE GREATER OF: (A) THE AMOUNTS YOU PAID TO US IN THE TWELVE MONTHS PRECEDING THE 
        CLAIM, OR (B) ONE HUNDRED CANADIAN DOLLARS (CAD $100).
      </p>

      <h3>10.4 User-Created Mini-Apps</h3>
      <p>
        We are not responsible for Mini-Apps created by users. When you use another user's Mini-App, 
        you do so at your own risk. We do not review, endorse, or guarantee the safety, functionality, 
        or legality of user-created Mini-Apps.
      </p>

      <h2>11. Indemnification</h2>
      <p>
        You agree to indemnify, defend, and hold harmless <a href="https://chapterpal.com" target="_blank" rel="noopener noreferrer" className="text-accent-500 hover:underline">ChapterPal Inc</a>., its affiliates, and their 
        respective officers, directors, employees, and agents from any claims, liabilities, damages, 
        losses, and expenses (including reasonable legal fees) arising from:
      </p>
      <ul>
        <li>Your User Content or Mini-Apps</li>
        <li>Your use of the Service</li>
        <li>Your violation of these Terms</li>
        <li>Your violation of any third-party rights</li>
        <li>Any claim that your Mini-App caused damage to a third party</li>
      </ul>

      <h2>12. Copyright and DMCA</h2>
      <p>
        We respect intellectual property rights and expect users to do the same. We may remove Mini-Apps 
        or content that infringe copyrights and may terminate accounts of repeat infringers.
      </p>
      <p>
        For information about reporting copyright infringement, please see our{' '}
        <Link to="/copyright">Copyright Policy</Link>.
      </p>

      <h2>13. Termination</h2>
      <p>
        We may terminate or suspend your account immediately, without prior notice, for any reason, 
        including breach of these Terms. You may delete your account at any time.
      </p>
      <p>
        Upon termination, your right to use the Service ceases immediately. We may retain certain 
        data as required by law or for legitimate business purposes. Published Mini-Apps may be 
        removed or remain accessible at our discretion.
      </p>

      <h2>14. Governing Law and Disputes</h2>
      <p>
        These Terms are governed by the laws of the Province of Quebec and the federal laws of Canada 
        applicable therein, without regard to conflict of law principles.
      </p>
      <p>
        Any disputes arising from these Terms or your use of the Service shall be resolved through 
        binding arbitration in Montreal, Quebec, Canada, except where prohibited by applicable consumer 
        protection law. You agree to waive any right to participate in class action lawsuits to the 
        extent permitted by law.
      </p>

      <h2>15. Changes to Terms</h2>
      <p>
        We may modify these Terms at any time. We will provide at least 30 days' notice before material 
        changes take effect via email or in-app notification. Your continued use of the Service after 
        changes become effective constitutes acceptance of the modified Terms.
      </p>

      <h2>16. Contact</h2>
      <p>
        For questions about these Terms, please contact us at{' '}
        <a href="mailto:legal@llambada.com">legal@llambada.com</a>.
      </p>
    </InfoPageLayout>
  );
}
